import { useEffect, useRef } from "react";

/** Șina verticală a secțiunii de proces, și aprinderea ordinalelor.
 *
 *  Linia se umple pe măsură ce derulezi prin cei cinci pași. Când umplerea
 *  trece de un ordinal, cifra — până atunci doar contur — se umple: se aprinde
 *  o clipă la temperatura caldă, apoi se răcește la cerneală și RĂMÂNE plină.
 *  E același principiu ca materialul din hero: temperatura se disipă,
 *  deformarea rămâne. Odată parcurs un pas, nu se mai golește.
 *
 *  De ce din JavaScript și nu cu `animation-timeline: view()`, care ar rula pe
 *  compozitor: Firefox încă nu îl are. O animație care merge doar în Chrome ar
 *  însemna că jumătate dintre vizitatori văd un contur care nu se umple
 *  niciodată.
 *
 *  Costul e ținut mic: ascultătorul de scroll se atașează doar cât secțiunea e
 *  în preajma ferestrei, calculul e limitat la un cadru prin rAF, iar poziția
 *  ordinalelor se măsoară o dată la intrare, nu la fiecare cadru. */
export default function StepsRail() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    const steps = el?.parentElement;
    if (!el || !steps) return;
    const ords = Array.from(steps.querySelectorAll<HTMLElement>(".ord"));

    const ignite = (o: HTMLElement) => {
      if (o.dataset.lit) return;
      o.dataset.lit = "1";
      o.classList.add("lit", "hot");
      /* Două cadre, ca tranziția de răcire să aibă de unde pleca: setate în
         același cadru, browserul ar sări direct la starea finală. */
      requestAnimationFrame(() => requestAnimationFrame(() => o.classList.remove("hot")));
    };

    if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
      el.style.setProperty("--p", "1");
      ords.forEach((o) => { o.dataset.lit = "1"; o.classList.add("lit"); });
      return;
    }

    let raf = 0;
    let listening = false;
    let offs: number[] = [];
    let top = 0;
    let h = 1;

    const measure = () => {
      const r = steps.getBoundingClientRect();
      top = r.top + window.scrollY;
      h = r.height || 1;
      offs = ords.map((o) => {
        const b = o.getBoundingClientRect();
        return b.top + window.scrollY + b.height * 0.45 - top;
      });
    };

    const update = () => {
      raf = 0;
      /* Linia de aprindere e la 62% din fereastră: un pas se aprinde când ai
         ajuns cu privirea la el, nu când i-a intrat marginea de sus în ecran. */
      const p = Math.min(h, Math.max(0, window.scrollY + window.innerHeight * 0.62 - top));
      el.style.setProperty("--p", (p / h).toFixed(4));
      for (let i = 0; i < ords.length; i++) if (p >= offs[i]) ignite(ords[i]);
    };

    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const onResize = () => { measure(); onScroll(); };

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries[0].isIntersecting;
        if (visible === listening) return;
        listening = visible;
        if (visible) {
          measure();
          window.addEventListener("scroll", onScroll, { passive: true });
          update();
        } else {
          window.removeEventListener("scroll", onScroll);
        }
      },
      { rootMargin: "250px 0px" },
    );
    io.observe(steps);
    window.addEventListener("resize", onResize, { passive: true });
    measure();
    update();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <div className="stepsrail" ref={ref} aria-hidden="true"><i /></div>;
}
